<template>
  <!-- Left Sidebar Menu -->
  <div class="sidebar-menu" :class="{'d-none d-md-block': !showMenu}">
    <div v-if="subscriptionExpired" class="alert alert-danger trial-popup mobile" role="alert">
      Subscription expired. <router-link to="/subscription" class="text-decoration-underline">Upgrade now</router-link>.
    </div>
    <div v-else-if="showTrialPopup" class="alert alert-danger trial-popup mobile" role="alert">
      You're on trial. <router-link class="text-decoration-underline" to="/subscription">Upgrade now</router-link> to enhance your chances.
    </div>

    <div class="menus-col" style="padding-bottom: 0 !important;">
      <div class="chat-menus">
        <ul>
          <li v-if="!isOperator">
            <router-link
              to="/discover"
              class="chat-unread"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              :title="$t('navbar_discover_hover')"
              :data-bs-original-title="$t('navbar_discover_hover')"
            >
              <a-tooltip :mouseEnterDelay="0" :mouseLeaveDelay="0" title="Discover people" placement="right">
                <i class="bx bx-group"></i>
              </a-tooltip>

              <p class="text d-none">{{$t('chat_discover_button')}}</p>
            </router-link>
          </li>

          <li>
            <router-link
              to="/chats"
              :class="{'active router-link-exact-active': isChatRoute}"
              class="chat-unread"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              title="Chat"
              data-bs-original-title="Chat"
              ref="tooltipTrigger"
            >
              <div v-if="newMessagesCount" class="new-message-count-bullet">{{ newMessagesCount }}</div>

              <div>
                <a-tooltip :mouseEnterDelay="0" :mouseLeaveDelay="0" title="Chat" placement="right">
                  <i :class="{'new-message-text': newMessagesCount}" class="bx bx-message-square-dots"></i>
                </a-tooltip>

                <p class="text d-none">{{$t('navbar_chat_hover')}}</p>
              </div>
            </router-link>
          </li>

          <li v-if="!isOperator">
            <router-link
              to="/subscription"
              class="chat-unread"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              title="Subscription"
              data-bs-original-title="Subscription"
            >
              <a-tooltip :mouseEnterDelay="0" :mouseLeaveDelay="0" title="Subscription" placement="right">
                <i class="bx bx-right-arrow"></i>
              </a-tooltip>

              <p class="text d-none">{{$t('navbar_subscription_hover')}}</p>
            </router-link>
          </li>

          <li>
            <router-link
              to="/settings"
              class="chat-unread"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              title="Settings"
              data-bs-original-title="Settings"
            >
              <a-tooltip :mouseEnterDelay="0" :mouseLeaveDelay="0" title="Settings" placement="right">
                <i class="bx bx-cog"></i>
              </a-tooltip>

              <p class="text d-none">{{$t('navbar_settings_hover')}}</p>
            </router-link>
          </li>

          <li class="d-md-none">
            <router-link
              to="/profile"
              class="chat-unread"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              title="Profile"
              data-bs-original-title="Profile"
            >
              <UserAvatar :user="currentUser"/>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="bottom-menus">
        <ul>
          <li>
            <div class="avatar avatar-online">
              <a href="#" class="chat-profile-icon" data-bs-toggle="dropdown">
                <UserAvatar :user="currentUser"/>
              </a>

              <div class="dropdown-menu dropdown-menu-end">
                <router-link v-if="!isOperator" to="/profile" class="dropdown-item">
                  <span><i class="bx bx-cog"></i></span>
                  {{$t('profile_button')}}
                </router-link>

                <a href="#" class="dropdown-item" @click.prevent="logout">
                  <span><i class="bx bx-log-out"></i></span>
                  {{$t('logout')}}
                </a>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <!-- /Left Sidebar Menu -->
</template>

<script>
import { Tooltip, Button } from "ant-design-vue";
import {mapGetters} from "vuex";
import UserAvatar from "@/components/frontend/UserAvatar";

export default {
  components: {
    UserAvatar,
    "a-tooltip": Tooltip,
    "a-button": Button,
  },

  watch: {
    internalUsers: {
      handler() {},
      immediate: true,
    },

    isDarkMode: (newVal) => {
      if (newVal) {
        document.body.classList.add("darkmode");
      } else {
        document.body.classList.remove("darkmode");
      }
    }
  },

  data() {
    return {
      isDarkMode: false,
      showMenu: true,
    }
  },

  computed: {
    ...mapGetters({
      subscriptionExpired: 'subscriptions/expired',
      internalUsers: 'chat/internalUsers',
      newMessagesCount: 'chat/newMessagesCount',
      newMessagesCountForUser: 'chat/newMessagesCountForUser',
      currentUser: 'auth/user',
      isOperator: 'isOperator',
    }),

    showTrialPopup() {
      if (this.$store.getters['isOperator']) {
        return false;
      }

      return this.currentUser && this.currentUser.subscription && this.currentUser.subscription.plan.name.toLowerCase() === 'trial'
        && [
          'home',
          'chats',
          'settings',
          'discover',
          'profile',
          'subscription',
          'user-profile',
        ].includes(this.$route.name);
    },

    isChatRoute() {
      return this.$route.path.includes('/chat');
    },

    currentPath() {
      return this.$route.name;
    },
  },

  mounted() {
    this.$eventHub.$on('toggleMenu', this.onToggleMenu);

    this.initializeDarkMode();

    this.$nextTick(() => {
      if (this.$route.name === 'chat-conversation') {
        this.showMenu = false;
      }
    });
  },

  methods: {
    toggleDarkMode() {
      if (this.isDarkMode) {
        document.body.classList.remove("darkmode");
        localStorage.setItem("darkMode", null);
      } else {
        document.body.classList.add("darkmode");
        localStorage.setItem("darkMode", "enabled");
      }
      this.isDarkMode = !this.isDarkMode;
    },

    initializeDarkMode() {
      const darkMode = localStorage.getItem("darkMode");
      this.isDarkMode = darkMode === "enabled";
    },

    onToggleMenu(state = null) {
      this.showMenu = window.innerWidth >= 767 || state;
    },

    async logout() {
      await this.$store.dispatch('auth/logout');
      window.location.href = '/login';
    },
  }
};
</script>
